import { Clipboard } from '@angular/cdk/clipboard';
import { AfterViewInit, Type as AngularCoreType, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MultipleSortSettings, RowReorderEvent, SelectAllCheckboxState } from "@progress/kendo-angular-grid";
import { ContextMenuComponent } from "@progress/kendo-angular-menu";
import { AggregateDescriptor, FilterDescriptor, GroupDescriptor, SortDescriptor, State } from "@progress/kendo-data-query";
import { Observable, Subject } from "rxjs";
import { AlertDialogComponent, AlertDialogModel } from '../../../dialogs/alert/alert-dialog.component';
import { BulkWorkflowDialog, BulkWorkflowDialogInitData } from '../../../dialogs/bulk-workflow/bulk-workflow.dialog';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../dialogs/confirmation/confirmation-dialog.component';
import { DocumentPublishingPickerDialog } from '../../../dialogs/document-publishing/document-publishing-picker.dialog';
import { ExportDataDialog, ExportDataDialogAction, ExportDataDialogConfig } from '../../../dialogs/export-data/export-data.dialog';
import { HomePageActionDialogReturnObj, HomePageActionsEnum } from '../../../dialogs/home-page-action/extras';
import { HomePageActionDialog, HomePageActionDialogInitData } from '../../../dialogs/home-page-action/home-page-action.dialog';
import { FormTemplateInstancesDirectiveData, FormTemplateInstancesGridRemoteBindingDirective } from '../../../directives/form-template-instances-remote.directive';
import { EventNameEnum } from '../../../enums/event-subscription.enum';
import { IFieldDefinitionLogic } from '../../../interfaces/ifield-definition-logic.interface';
import { KendoGridHelper } from '../../../kendo-grid-helper';
import { EventSubscription } from '../../../models/event-subscription.model';
import { FieldDefinition } from '../../../models/form-builder/field-definition.model';
import { FormFieldOnInitPropertyEnum } from '../../../models/form-builder/form-field-on-init-output-property.enum';
import { FormFieldPropertyEnum } from '../../../models/form-builder/form-field-property-enum.model';
import { FormField } from '../../../models/form-builder/form-field.model';
import { FormTemplateInstancesButtons } from '../../../models/form-template-instances/form-template-instances-buttons';
import { FormTemplateInstancesConfig } from '../../../models/form-template-instances/form-template-instances-config.model';
import { FormTemplatePropertyInfo } from '../../../models/form-template-instances/form-template-property-info';
import { IMenuItem } from '../../../models/form-template-instances/menu-item.models';
import { MetadataFieldFactory } from '../../../models/form-template-instances/metadata-field-factory.model';
import { SubscriptionFilter } from '../../../models/notifications/subscription-filter.model';
import { FormInstance } from '../../../models/site-content/form-instance.model';
import { FormTemplateInstancesGridData } from '../../../models/site-content/form-template-instances-grid-data.model';
import { BrowserStorageService } from '../../../services/browserstorage.service';
import { CurrentSiteService } from '../../../services/current-site.service';
import { DocumentPublishingService } from '../../../services/document-publishing.service';
import { EventSubscriptionService } from '../../../services/event-subscription.service';
import { FieldDefinitionService } from '../../../services/field-definition.service';
import { FolderService } from '../../../services/folder.service';
import { FormInstanceService } from '../../../services/form-instance.service';
import { FormTemplateInstancesService, MapOfFormsGridOrders, OrderFormsGridRowsData } from '../../../services/form-template-instances.service';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { FlexFieldInternalNameToValue, FlexibleSelectionFieldService } from '../../../services/flexible-selection-field.service';
import { GridFieldType } from '../../../models/form-builder/form-field-types';
import { column, forEach } from 'mathjs';
import { UserHomePagePermissions } from '../../../models/site-content/user-home-page-permissions.model';

class FormTemplateInstanceKendoGridData {
    public editedRowIndex: number;

    public view: Observable<FormInstance>;
    public gridState: State = {
        sort: [],
        skip: 0,
        take: 100,
        filter: {
            filters: [],
            logic: 'and'
        }
    };

    public savedStateRestored: boolean = false;

    public groups: GroupDescriptor[] = [];

    public appliedFilters: FilterDescriptor[] = []; // Note:  a filter can be applied using field conditional logic.
    public calledRebindAfterAppliedFilters: boolean = false; // Note
}

class TemplateInstancesGridStateManager {

    private static readonly templateInstancesGridStateKey: string = 'TemplateInstancesGrid';

    public static saveGridState(browserStorageService: BrowserStorageService, formField: FormField, state: State): void {
        if (state != null) {
            let stateJson: string = JSON.stringify(state);
            browserStorageService.set(TemplateInstancesGridStateManager.getKey(formField), stateJson);
        }
    }

    public static getGridState(browserStorageService: BrowserStorageService, formField: FormField): State {
        let result: State = null;
        let savedStateJson: string = browserStorageService.get(TemplateInstancesGridStateManager.getKey(formField));

        if (savedStateJson != null) {
            result = <State>JSON.parse(savedStateJson);
        }

        return result;
    }

    public static deleteGridState(browserStorageService: BrowserStorageService, formField: FormField, state: any) {
        browserStorageService.delete(TemplateInstancesGridStateManager.getKey(formField));
    }

    private static getKey(formField: FormField): string {
        return `${TemplateInstancesGridStateManager.templateInstancesGridStateKey}.${formField.id}`;
    }
}

class ConfiguredColDefs {
    // Properties.
    private configuredColDefs: FormField[] = [];
    private configuredColumnNames: string[] = [];
    private aggregates: AggregateDescriptor[] = [];

    private jsonConfig: FormTemplateInstancesConfig = null;

    // Read-only properties.
    private readonly allowableAggregateNames: string[] = ['sum', 'count'];

    // Constructor.
    public constructor() {
    }

    // Methods.
    // Getter methods.
    public get CommandColumnPostion(): string {
        return this.jsonConfig?.CommandColumnPostion;
    }

    public get ConfiguredColDefs(): FormField[] {
        return this.configuredColDefs;
    }

    public get ConfiguredColumnNames(): string[] {
        return this.configuredColumnNames;
    }



    // Ordinary methods.
    public buildColDefs(fieldDefinitionService: FieldDefinitionService, formFields: FormField[], jsonConfig: FormTemplateInstancesConfig): void {
        this.jsonConfig = jsonConfig;
        let properties = this.jsonConfig.SelectedFormTemplateProperties;
        for (let index: number = 0; index < properties.length; index++) {
            let propName = properties[index].propertyName;
            let fieldId = properties[index].fieldId;
            let alias = properties[index].alias;
            let columnWidth = properties[index].columnWidth;
            let associatedFlexFieldName: string = properties[index].associatedFlexFieldName;
            this.configuredColumnNames.push(propName);
            let field = this.getField(fieldId, propName, alias, columnWidth, formFields, associatedFlexFieldName);
            if (field) {
                this.configuredColDefs.push(field);

                let fieldDefinition: FieldDefinition = fieldDefinitionService.getFieldDefinition(field.fieldDefinitionClassName);
                if (fieldDefinition.isNumeric) {
                    let aggregateNames: string = properties[index].optionalAggregateNames;
                    if ((aggregateNames != null) && (aggregateNames.trim() != '')) {
                        // Add any valid, non-duplicate aggregates.
                        this.addAnyNumericFieldAggregates(propName, aggregateNames);
                    }
                }
            }
        } // for
    }

    // Helper methods.
    private getField(fieldId: number, propName: string, alias: string, columnWidth: number, fields: FormField[], associatedFlexFieldName: string): FormField {
        let field = this.getMetadataField(propName);

        if (!field) {
            var key = `${fieldId}_${propName}`;
            field = fields.filter(x => { return `${x.id}_${x.displayName || x.name}` == key })[0];
        }
        if (alias?.trim().length > 0) {
            field.alias = alias;
        }
        if (field && columnWidth > 0) {
            field.columnWidth = columnWidth;
        }
        if (field != null && associatedFlexFieldName?.trim().length > 0) {
            field.associatedFlexFieldName = associatedFlexFieldName;
        }
        return field;
    }

    private getMetadataField(propName: string): FormField {
        let metadataDummyFormFields: FormField[] = MetadataFieldFactory.getDummyMetaDataFormFields();
        let field = metadataDummyFormFields.filter(f => { return f.name == propName || f.displayName == propName });
        return field[0];
    }

    private addAnyNumericFieldAggregates(propertyName: string, aggregateNames: string): void {
        let splitAggregateNames: string[] = aggregateNames.split('|');
        for (let index: number = 0; index < splitAggregateNames.length; index++) {
            let aggregateName: string = splitAggregateNames[index];
            if (this.allowableAggregateNames.indexOf(aggregateName) >= 0) {
                let validAggregateName: 'count' | 'sum' | 'average' | 'min' | 'max' = <'count' | 'sum' | 'average' | 'min' | 'max'>aggregateName;
                let aggregate: AggregateDescriptor = {
                    field: propertyName,
                    aggregate: validAggregateName
                };
                this.aggregates.push(aggregate);
            }
        }
    }
}

// Define menu item name constants.
const Open_MenuItem: string = "Open";
const CopyLink_MenuItem: string = "Copy Link";
const Delete_MenuItem: string = "Delete ...";
const Copy_MenuItem: string = "Copy ...";
const Move_MenuItem: string = "Move ...";
const PublishForm_MenuItem: string = "Publish Form ...";
const WorkflowTransition_MenuItem: string = "Workflow Transition";

// Questions to ask on the 03/08/2024 morning call:
//     1. Predefined Filters:  how to handle them;
//     2. Grid State/Config:  how to know when it changes;
//     3. Database Migration:  is anyone else working on one?
//     4. Reusable Grid Download Code:  could a service that support a grid download provide an adapter that conforms to an interface that can be used to write service data as Excel workbook rows?
//     5. Why isn't rebind() called after the configuration is parsed/loaded?
//     6. Why is get PredefinedFilters() not getting called?

@Component({
    selector: 'app-form-template-instances',
    templateUrl: './form-template-instances.component.html',
    styleUrls: ['./form-template-instances.component.scss'],
    encapsulation: ViewEncapsulation.None, //enable CSS overrides
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FormTemplateInstancesComponent,
            multi: true
        }
    ],
    standalone: false
})
export class FormTemplateInstancesComponent extends FormFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {
    // Properties.
    // Properties.
    // Output.
    @Output() onInit = new EventEmitter();
    @Output() newFormInstanceCreated = new EventEmitter();

    // Get a reference to the remote binding directive that handles all the changes to Grid state (Relates to <kendo-grid #grid="remoteBindingDirectiveRef" in the HTML)
    @ViewChild(FormTemplateInstancesGridRemoteBindingDirective) public remoteBindingDirectiveRef: FormTemplateInstancesGridRemoteBindingDirective;
    @ViewChild("gridmenu") private gridContextMenu: ContextMenuComponent;
    @ViewChild('formNameInput') formNameInput: ElementRef;

    private childLoadedTimeout: any = 0;

    private helper = new KendoGridHelper();
    private metadataDummyFormFields: FormField[] = [];
    private kendoGridData: FormTemplateInstanceKendoGridData = new FormTemplateInstanceKendoGridData();
    private configuredColumnNames: string[] = [];
    private configuredColDefs: FormField[] = [];
    private newConfiguredColDefs: ConfiguredColDefs = new ConfiguredColDefs();
    public selectAllState: SelectAllCheckboxState = 'unchecked';
    private showGridFilter: boolean = false;
    private rowIndexClicked: number = -1;
    private formRenameInProgress: boolean = false;
    private userClickedOnNameColumn: boolean = false;

    //VNEXT-980: KLW - Property to set the number of Kendo grid rows to display
    public rowHeight: number = 36;
    private toolbarHeadersFilters = 132; // height of grid with no rows
    private detailRowHeight: number = 0;
    private extraHeightWhenEditingRow: number = 0;

    // 07-01-2024 note:  a few weeks ago I consolidated the numerous directive inputs into a single class/single input (called 'inputs').
    private directiveInputs: FormTemplateInstancesDirectiveData = new FormTemplateInstancesDirectiveData();

    //private canReorderFormsData: CanReorderFormsData = new CanReorderFormsData(); // VNEXT-1666.
    private userHomePagePermissions: UserHomePagePermissions = null;

    // Read only proeprties.
    private readonly formFieldProperties: string[] =
        [
            FormFieldPropertyEnum.DISPLAY_NAME,
            FormFieldPropertyEnum.TOOL_TIP,
            FormFieldPropertyEnum.FORM_TEMPLATE,
            FormFieldPropertyEnum.FORM_TEMPLATE_PROPERTIES,
            FormFieldPropertyEnum.INSTRUCTIONS_TEXT,
            FormFieldPropertyEnum.NO_DEFAULT_PROPERTIES,
            FormFieldPropertyEnum.DISPLAY_KENDO_GRID_ROWS, //VNEXT-980: KLW - Property to set the number of Kendo grid rows to display
            FormFieldPropertyEnum.OPEN_FORM_INSTANCES_IN_NEW_TAB,
            FormFieldPropertyEnum.FORM_TEMPLATE_INSTANCE_ACTION_BUTTONS_CONFIG,
            FormFieldPropertyEnum.HIDE_GRID_FILTERING, // https://maxjira.max.gov/browse/VNEXT-1252
            FormFieldPropertyEnum.DISABLE_GRID_GROUPING, // https://maxjira.max.gov/browse/VNEXT-1271
            FormFieldPropertyEnum.EXCEL_IMPORT_EXPORT_TEMPLATE, // https://maxjira.max.gov/browse/VNEXT-1154
            FormFieldPropertyEnum.ENABLE_SIMPLE_FORM_GRID_DISPLAY,
            FormFieldPropertyEnum.PRESET_FILTERS
        ];

    private fieldsInitialized: boolean = false;
    private writeableFolders: number[];
    private message: string;
    private initialized: boolean = false;
    private showLoading: boolean = true;
    private gridMode: string;

    public selectedRows: any[] = []; // Let's assign a type other than 'any' to this property.
    // this callback is used by Kendo Grid to determine what objects are used by its [(selectedKeys)] two way binding
    // the end result is that we get full-blown dataItems in selectedRows rather than the default of row indexes
    public selectedCallback = (args) => args.dataItem;
    // delete, copy, move etc
    private action: string;
    private gridData: FormTemplateInstancesGridData;
    private doNotHideButtonsForTheseActions: string[] = [HomePageActionsEnum.ACTION_CREATE_FORM, HomePageActionsEnum.ACTION_CREATE_FOLDER];
    private indexOfSelectedFormToPublish: number = null;

    private templateInstancesButtons: FormTemplateInstancesButtons = new FormTemplateInstancesButtons();
    private aggregates: AggregateDescriptor[] = [];

    private readonly allowableAggregateNames: string[] = ['sum', 'count']; // Note:  presently we are not allowing 'min', 'max', or 'avg'.

    private readonly menuItems: IMenuItem[] =
        [
            { text: Open_MenuItem },
            { text: CopyLink_MenuItem },
            { text: Delete_MenuItem },
            { text: Copy_MenuItem },
            { text: Move_MenuItem },
            { text: PublishForm_MenuItem },
        ];
    private flexFieldInternalNamesToValuesSubscription: any;

    // Constructor.
    public constructor(
        private formTemplateInstancesService: FormTemplateInstancesService,
        private fieldDefinitionService: FieldDefinitionService,
        private folderService: FolderService,
        private formInstanceService: FormInstanceService,
        private currentSiteService: CurrentSiteService,
        private eventSubscriptionService: EventSubscriptionService,
        private documentPublishingService: DocumentPublishingService,
        private flexibleSelectionFieldService: FlexibleSelectionFieldService,
        private browserStorageService: BrowserStorageService,
        private clipboard: Clipboard,
        private router: Router,
        private dialog: MatDialog) {
        super();
    }

    // Life cycle methods.
    public ngOnInit(): void {
        // Emit my onInit() output.
        let hshOnInitProperties = this.getProperties();
        this.onInit.emit(hshOnInitProperties);

        // Send a request to see if the user can reorder forms (as this has been a problematic property to set dynamically).
        this.formTemplateInstancesService.getUserFormsGridPermissions(this.FormField.id).then(response => {
            this.userHomePagePermissions = response;
        });

        // Populate dummy form fields for metadata columns
        this.metadataDummyFormFields = MetadataFieldFactory.getDummyMetaDataFormFields();

        // Do we have a form template.
        if (this.formField.formTemplateId)
            this.retrieveFormTemplateAndColumnDefs();

        this.folderService.getWriteableFolderIds(this.currentSiteService.Site.id).then(x => {
            this.writeableFolders = x;
        });

        // Get information about configured buttons.
        this.templateInstancesButtons.createButtonInfoFromSavedConfig(this.formField.formTemplateInstanceActionButtonsConfig);

        // PJH - 3/22/2024 - added for VNEXT-1645
        this.flexFieldInternalNamesToValuesSubscription = this.flexibleSelectionFieldService.FlexFieldInternalNamesToValues.subscribe((filterValues: FlexFieldInternalNameToValue) => {
            /**
             * filterValues - Contains the flexfield internal name and its filter values
             * configuredColDefs - Contains the associatedFlexFieldName (internal name) values that were assigned to the grid cols
             */

            let filterList: FilterDescriptor[] = [];
            let filterOperator: string = "contains";

            //***Create list of filter Descriptor items
            if (filterValues != null && Object.entries(filterValues).length > 0) {
                for (const [key, value] of Object.entries(filterValues)) {
                    let filterValue = value[0];
                    let flexFieldId = key;
                    let columnToFilter = this.configuredColDefs.find(x => x.associatedFlexFieldName == flexFieldId)?.displayName;

                    if (columnToFilter != null && columnToFilter.length > 0) {
                        let filterDescriptor = this.getFilterDescriptor(filterValue, filterOperator, columnToFilter);

                        if (filterDescriptor != null)
                            filterList.push(filterDescriptor);
                    }
                }
            }

            //***Apply the filter to the grid
            this.applyFilterList(filterList);

        });
    }

    // PJH - 3/22/2024 - added for VNEXT-1645
    public ngOnDestroy() {
        this.flexFieldInternalNamesToValuesSubscription.unsubscribe();
    }

    public ngAfterViewInit(): void {
    }

    // 1) selectedRows will be ALL rows, so I need to unselect by resetting this.selectedRows to only those
    // that can be selected
    public onSelectAllChange(checkedState: SelectAllCheckboxState) {
        if (checkedState === 'checked') {
            this.selectedRows = this.selectedRows.filter(x => !this.isDisabled(x));
            this.selectAllState = 'checked';
        } else {
            this.selectedRows = [];
            this.selectAllState = 'unchecked';
        }
    }

    public handleLoaded(event: any) {
        this.helper.handleLoaded(event);

        if (!this.initialized) {
            this.initialized = this.helper.CurrentlyLoadedRowCount > 0 || event.gridData.canPerformAtLeastOneAction();
            this.gridData = event.gridData;
        }

        // make sure nothing is selected
        this.selectAllState = 'unchecked';
        this.selectedRows = [];

        if (event.directive != null) {
            let directive: FormTemplateInstancesGridRemoteBindingDirective = <FormTemplateInstancesGridRemoteBindingDirective>event.directive;

            if ((this.Mode === 'preview') || (this.Mode === 'instance')) {
                if (!this.kendoGridData.savedStateRestored) {
                    // Restore any saved home page.
                    this.restoreAnySavedGridState(directive);

                    this.kendoGridData.savedStateRestored = true;
                }

                if ((this.kendoGridData.appliedFilters != null) && (this.kendoGridData.appliedFilters.length > 0)) {
                    for (let index: number = 0; index < this.kendoGridData.appliedFilters.length; index++) {
                        let appliedFilter: FilterDescriptor = this.kendoGridData.appliedFilters[index];
                        if (!FormTemplateInstancesComponent.filterExists(appliedFilter, this.kendoGridData.gridState.filter.filters)) {
                            this.kendoGridData.gridState.filter.filters.push(appliedFilter);
                        }
                    }
                }
            }

            directive.inputs = this.DirectiveInputs;
        }
    }
    public isCompoundObjectComponent(): boolean {
        // Note:  this method is also used in apply conditional logic.
        return true;
    }
    public applyFilter(filterValue: string, filterOperator: string, childFieldName: string): void {
        let filter: FilterDescriptor = {
            field: childFieldName,
            value: filterValue,
            operator: filterOperator
        };

        if (!FormTemplateInstancesComponent.filterExists(filter, this.kendoGridData.gridState.filter.filters)) {
            this.kendoGridData.gridState.filter.filters.push(filter);
            this.kendoGridData.appliedFilters.push(filter);
        }

        // Set an interval/recurring timeout to check to see if we have the properties needed to execute this forms grid's query via rebind().
        // During the timeout, method applyFilterIfApplicable() checks to see if it has what it needs in order to call rebind().  If it does,
        // it calls rebind(), iniitiating the query, and lets us know that we can clear/cancel the interval.
        this.kendoGridData.calledRebindAfterAppliedFilters = false;
        this.childLoadedTimeout = setInterval(() => {
            if (this.remoteBindingDirectiveRef != null) {
                let canClearInterval: boolean = this.applyFilterIfApplicable();
                if (canClearInterval)
                    clearInterval(this.childLoadedTimeout);
            }
        }, 100);
    }

    /**
     * Uses specified list of filters to apply to the grid on the form
     * @param filterList - contains a list of filter objects
     */
    public applyFilterList(filterList: FilterDescriptor[]): void {
        /***
         * Clears the filter for the directive's state
         * Also clears this.kendoGridData.gridState.filter.filters for the first grid
         * on the page. Not sure why it doesn't clear for all other grids, but there
         * is logic inside of applyFilterList() which clears the filter data for all other grids
         */
        this.remoteBindingDirectiveRef.ClearDirectiveState();

        //***Clears the filter data for the form grid if was cleared from statement above
        this.kendoGridData.gridState.filter.filters = [];
        this.kendoGridData.appliedFilters = [];

        //***Add filters new filters to the gridState
        filterList.forEach(filter => {
            this.kendoGridData.gridState.filter.filters.push(filter);  //***auto adds the this.state.filter value for the remote directive
            this.kendoGridData.appliedFilters.push(filter);
        });

        // Set an interval/recurring timeout to check to see if we have the properties needed to execute this forms grid's query via rebind().
        // During the timeout, method applyFilterIfApplicable() checks to see if it has what it needs in order to call rebind().  If it does,
        // it calls rebind(), iniitiating the query, and lets us know that we can clear/cancel the interval.
        this.kendoGridData.calledRebindAfterAppliedFilters = false;
        this.childLoadedTimeout = setInterval(() => {
            if (this.remoteBindingDirectiveRef != null) {
                let canClearInterval: boolean = this.applyFilterIfApplicable();
                if (canClearInterval)
                    clearInterval(this.childLoadedTimeout);
            }
        }, 100);
    }

    /**
     * Creates and returns a FilterDescriptor object using the provided values
     * @param filterValue - value to filter by
     * @param filterOperator - ex: contains, eq, ...
     * @param childFieldName - target column name
     * @returns
     */
    public getFilterDescriptor(filterValue: string, filterOperator: string, childFieldName: string): FilterDescriptor {
        let filter: FilterDescriptor = {
            field: childFieldName,
            value: filterValue,
            operator: filterOperator
        };

        return filter;
    }

    public applyChildFieldAttributes(childFieldName: string, showChildField: boolean, childFieldIsReadOnly: boolean, childFieldIsRequired: boolean): void {
        // TO DO:  implement this method.
    }

    private static filterExists(filter: FilterDescriptor, filters: object[]): boolean {
        let existingFilter: FilterDescriptor = null;

        for (let index: number = 0; index < filters.length; index++) {
            let searchFilter: FilterDescriptor = <FilterDescriptor>filters[index];
            if ((searchFilter.field == filter.field) && (searchFilter.value == filter.value) && (searchFilter.operator == filter.operator)) {
                existingFilter = searchFilter;
                break;
            }
        }

        return existingFilter != null;
    }

    public dataStateChange(state: any): void {
        // If you have a header that is not sorted and you ...
        // Click once:      it sorts ascending
        // Click twice:     it sorts descending
        // Click thrice:    it turns sorting off for that column BUT Kendo does not remove the object from the state.sort array, rather it simply sets the 'dir' property to undefined
        //                  so below we detect that scenario and set state.sort to an empty array, so that's what gets stored in the browser storage
        //                  (The main motivation for this was to provide a way out of the "changed after checked" error which happens in devl if you store sort state and come back to the SHP)
        let sortFound = state.sort.find(x => { return x.dir !== undefined && x.field !== undefined });
        if (!sortFound) {
            state.sort = [];
            this.kendoGridData.gridState.sort = [];
        } else {
            //add new sort if not present
            this.kendoGridData.gridState.sort = sortFound;
        }
        TemplateInstancesGridStateManager.saveGridState(this.browserStorageService, this.FormField, state);
    }

    public isGroupable(eventData: any): boolean {
        console.log('Code this');
        return true;
    }
    public get Groups(): GroupDescriptor[] {
        return this.kendoGridData.groups;
    }
    public set Groups(value: GroupDescriptor[]) {
        this.kendoGridData.groups = value;
    }
    public get HasGrouping(): boolean {
        return (this.kendoGridData.groups != null) && (this.kendoGridData.groups.length > 0);
    }
    public groupChange(groups: GroupDescriptor[]): void {
        this.kendoGridData.groups = groups;
        if (this.remoteBindingDirectiveRef != null) {
            if ((this.kendoGridData.groups != null) && (this.kendoGridData.groups.length > 0)) {
                for (let index: number = 0; index < this.kendoGridData.groups.length; index++)
                    this.kendoGridData.groups[index].aggregates = this.Aggregates;
            }
            this.remoteBindingDirectiveRef.rebind();
        }
    }

    public handleRowReorder(event: RowReorderEvent): void {
        //grid sorting on form load:
        //1. restoreAnySavedGridState():
        //   check browser storage for saved column sort in TemplateInstancesGrid.123 where 123 is id of FormTemplateInstances FormField
        //2. retrieveFormTemplateAndColumnDefs():
        //   if no browser storage value use FormField.FormsGridOrdersJson from database
        //
        //on grid reorder:
        //alert to remove any existing column sort before reordering

        let sortColumnMessage = '';
        if (Array.isArray(this.kendoGridData.gridState.sort)) { //  && this.kendoGridData.gridState.sort.length > 0) {
            let sortedColumns = [];
            this.kendoGridData.gridState.sort.forEach(s => {
                sortedColumns.push(s.field);
            });
            if (sortedColumns.length > 0) {
                sortColumnMessage = "Before reordering rows, please remove sort on grid columns " + sortedColumns.toString() + ".";
            }
        }
        else if (this.kendoGridData.gridState.sort) {
            let sd: SortDescriptor = this.kendoGridData.gridState.sort;
            if (sd) {
                sortColumnMessage = "Before reordering rows, please remove sort on grid column " + sd.field + ".";
            }
        }

        if (sortColumnMessage) {
            this.dialog.open(AlertDialogComponent, {
                disableClose: false,
                maxWidth: "600px",
                data: new AlertDialogModel("Cannot reorder sorted rows",
                    sortColumnMessage,
                    null, null)
            });
            return;
        }

        // Find the dragged and dropped on object row indexes.
        let draggedRowIndex: number = -1;
        let droppedOnRowIndex: number = -1;

        for (let index: number = 0; index < this.gridData.resultsAsPlainObjects.length; index++) {
            let row: object = this.gridData.resultsAsPlainObjects[index];
            let checkForIndexesFound: boolean = false;
            if (row == event.draggedRows[0].dataItem) {
                draggedRowIndex = index;
                checkForIndexesFound = true;
            } else if (row == event.dropTargetRow.dataItem) {
                droppedOnRowIndex = index;
                checkForIndexesFound = true;
            }
            if (checkForIndexesFound && (draggedRowIndex >= 0) && (droppedOnRowIndex >= 0))
                break;
        }

        // Reposition the rows.
        let removedObjectArray: object[] = this.gridData.resultsAsPlainObjects.splice(draggedRowIndex, 1);
        let insertPosition: number = event.dropPosition == 'before' ? droppedOnRowIndex : droppedOnRowIndex + 1;
        this.gridData.resultsAsPlainObjects.splice(insertPosition, 0, removedObjectArray[0]);

        // Assign, keep track of 'formsGridOrder' values.
        let formsGridOrderData: OrderFormsGridRowsData[] = [];
        let mapOfFormsGridOrders: MapOfFormsGridOrders = {};
        for (let index: number = 0; index < this.gridData.resultsAsPlainObjects.length; index++) {
            let row: object = this.gridData.resultsAsPlainObjects[index];
            row['formsGridOrder'] = index;
            let formInstanceId: number = row['Id'];
            let orderInfo: OrderFormsGridRowsData = new OrderFormsGridRowsData(formInstanceId, index);
            formsGridOrderData.push(orderInfo);
            mapOfFormsGridOrders[formInstanceId] = index;
        }

        // Send a request to the server to assign the updated forms grid order values.
        let mapOfFormsGridOrdersJson: string = JSON.stringify(mapOfFormsGridOrders);
        this.formTemplateInstancesService.setFormsGridOrderValues(this.formField.id, mapOfFormsGridOrdersJson);
    }

    public turnOnGridFilter() {
        this.showGridFilter = true;
    }

    public turnOffGridFilter() {
        this.Helper.handleClearFilters();
        var newState: State = TemplateInstancesGridStateManager.getGridState(this.browserStorageService, this.FormField);
        if (newState) {
            newState.filter.filters = [];
            TemplateInstancesGridStateManager.saveGridState(this.browserStorageService, this.FormField, newState);
        }
        this.showGridFilter = false;
    }

    public columnDisplayName(columnDef: FormField): string {
        return (columnDef.displayName != null) && (columnDef.displayName.trim() != '') ? columnDef.displayName : columnDef.name;
    }

    public get CommandColumnPostion(): string {
        return this.newConfiguredColDefs.CommandColumnPostion;
    }

    public get Helper(): KendoGridHelper {
        return this.helper;
    }

    public get ShowInstructions(): boolean {
        return this.actionRequiresFormSelection() && this.selectedRows.length == 0;
    }

    public get SelectedRows(): any[] {
        return this.selectedRows;
    }

    public set SelectedRows(value) {
        this.selectedRows = value;
    }

    public get Message(): string {
        return this.message;
    }

    public get ReadyToShowGrid(): boolean {
        return this.initialized;
    }

    public get ShowLoading(): boolean {
        // now returns false (for VNEXT-1250)
        return false;
    }

    public get ShowHide(): string {
        return this.helper.CurrentlyLoadedRowCount > 0 ? "showGrid" : "hideGrid";
    }

    public isDisabled(item, idx: number = -1): boolean {

        if (!item.isFirstGridRow) {
            // to avoid confusion, this allows selecting of only the first row when displaying a Form Instance's multiple grid rows
            return true;
        } else if (this.action == HomePageActionsEnum.ACTION_DELETE) {
            return !item.canDelete;
        } else if (this.action == HomePageActionsEnum.ACTION_COPY) {
            return !(item.canMove || item.canCopy);
        } else if (this.action == HomePageActionsEnum.ACTION_MOVE) {
            return !item.canDelete;
        }
        else if (this.action == HomePageActionsEnum.ACTION_PUBLISH_FORM) {
            // disables all but the currently selected row if one is selected
            return (this.indexOfSelectedFormToPublish != null) && (this.indexOfSelectedFormToPublish != idx);
        } else {
            return false;
        }
    }

    public get WriteableFoldersCount(): number {
        return this.writeableFolders?.length;
    }

    public get MenuItems(): IMenuItem[] {
        let menuItemsForUser = this.menuItems;
        if (!this.CanDeleteForm)
            menuItemsForUser = menuItemsForUser.filter(mi => mi.text != Delete_MenuItem);
        if (!this.CanCopyForm)
            menuItemsForUser = menuItemsForUser.filter(mi => mi.text != Copy_MenuItem);
        if (!this.CanMoveForm)
            menuItemsForUser = menuItemsForUser.filter(mi => mi.text != Move_MenuItem);

        return menuItemsForUser;
    }

    // Override abstract methdos defined in my base class.
    public getProperties(): any {
        let hshEventProperties = {
            component: this,
            formField: this.FormField,
            properties: this.formFieldProperties,
            propertyUpdateRequired: true
        };
        hshEventProperties[FormFieldOnInitPropertyEnum.REQUIRED_PREVIEW_INSTANCE_MODE_HEIGHT] = 550;
        hshEventProperties[FormFieldOnInitPropertyEnum.REQUIRED_PREVIEW_INSTANCE_MODE_HEIGHT_UNIT] = 'px';

        return (hshEventProperties);
    }

    public getFormFieldClass(): AngularCoreType<any> {
        return (FormTemplateInstancesComponent);
    }

    // Handle the property updated callback.
    public propertyUpdated(formField: FormField, propertyName: string): void {
        if (propertyName == 'formTemplate') {
            if (formField.formTemplateId > 0)
                this.retrieveFormTemplateAndColumnDefs();
        } else if (propertyName == 'allProperties') {
            // The pseudo-property 'allProperties' indicates that all properties are being applied.
            this.retrieveFormTemplateAndColumnDefs();
        }

        return;
    }

    // Getters
    public get FormTemplateId(): number {
        return this.formField.formTemplateId;
    }

    public get FieldsInitialized(): boolean {
        return this.fieldsInitialized && (this.userHomePagePermissions != null);
    }

    public get GridHeight(): number {
        let toolbarAndHeadersAndFiltersHeight = 150;
        let rowCount = this.helper.CurrentlyLoadedRowCount;
        let height: number = rowCount <= 15 ? (rowCount * 36) + toolbarAndHeadersAndFiltersHeight : 540 + toolbarAndHeadersAndFiltersHeight;
        return height;
    }

    //VNEXT-980: KLW - Property to set the number of Kendo grid rows to display
    public GetGridHeight() {
        let rowCount = this.helper.CurrentlyLoadedRowCount;
        let rowsDisplayed: number = this.formField.GetDisplayKendoGridRows;

        let retVal = FormTemplateInstancesComponent.CalculateGridHeight(this.rowHeight,
            this.toolbarHeadersFilters,
            this.detailRowHeight,
            this.extraHeightWhenEditingRow,
            rowCount,
            rowsDisplayed,
            false,  // Wrapped header text not yet enabled.
            false); // A footer row is not enabled.

        return retVal;
    }

    public get PageSize(): number {
        let num = this.kendoGridData.gridState.take;
        return num;
    }

    public get KendoPageSize(): number {
        //let num = this.dataUsedWithKendoGrid.gridState.take;
        //VNEXT-980: KLW - Property to set the number of Kendo grid rows to display
        let num = this.formField.GetDisplayKendoGridRows;
        return num;
    }

    public get Skip(): number {
        let num = this.kendoGridData.gridState.skip;
        return num;
    }

    public get SortSettings(): any {
        let settings: MultipleSortSettings = {
            mode: "multiple",
            initialDirection: "desc",
            allowUnsort: true,
            showIndexes: true,
        };
        return settings;
    }
    public get Sort(): SortDescriptor[] {
        return this.kendoGridData.gridState.sort;
    }

    public get ShowGridFilter(): boolean {
        return this.showGridFilter;
    }

    public get RowIndexClicked(): number {
        return this.rowIndexClicked;
    }
    public get GridIsFilterable(): boolean {
        return !this.formField.hideGridFiltering;
    }
    public get EnableGridGrouping(): boolean {
        return !this.formField.disableGridGrouping;
    }

    public get ColumnNames(): string[] {
        let columnNames: string[] = this.configuredColumnNames.length > 0 ? this.configuredColumnNames : ['id', 'name', 'folderPath'];
        return columnNames;
    }

    public get ColumnDefs(): FormField[] {
        const fields = this.newConfiguredColDefs.ConfiguredColDefs.filter(x => { return x.name != 'Favorite' && x.name != 'Subscription' && x.name != 'LinkedFormInstance' });
        return fields;
    }

    public get IsFavoriteColumnDef(): FormField {
        let array = this.configuredColDefs.filter(x => { return x.name == 'Favorite' });
        if (array.length > 0) {
            return array[0];
        } else {
            return null;
        }
    }

    public get IsLinkedFormInstanceColumnDef(): FormField {
        let array = this.configuredColDefs.filter(x => { return x.name == 'LinkedFormInstance' });
        if (array.length > 0) {
            return array[0];
        } else {
            return null;
        }
    }

    public get SubscriptionIdColumnDef(): FormField {
        let array = this.configuredColDefs.filter(x => { return x.name == 'Subscription' });
        if (array.length > 0) {
            return array[0];
        } else {
            return null;
        }
    }

    public get UserCanReorderForms(): boolean {
        /*
        if (this.canReorderFormsData.canReorderPermissionCalculated) {
            return this.canReorderFormsData.canReorderForms;
        } else {
            let result: boolean = (this.gridData != null) && (this.currentSiteService.Site.siteIsAdministerable || this.gridData.canReorderForms);
            if ((this.gridData != null) && (this.gridData.canReorderForms != null)) {
                this.canReorderFormsData.canReorderForms = result;
                this.canReorderFormsData.canReorderPermissionCalculated = true;
            }
            return result;
        }
        */
        return this.userHomePagePermissions.userCanReorderForms;
    }

    // The id of the form field which contains the JSON which stores the properties to be included in the grid
    public get FormFieldId(): number {
        return this.formField.id;
    }

    public get Aggregates(): AggregateDescriptor[] {
        // Note:  aggregates need to be configured when configuring this component.
        return (this.kendoGridData.groups != null) && (this.kendoGridData.groups.length > 0) ? this.aggregates : [];
    }
    public getAggregateTitle(aggregateInfo: AggregateDescriptor): string {
        let aggregateFunctionName: string = aggregateInfo['aggregate'];
        if ((aggregateFunctionName != null) && (aggregateFunctionName.length > 0))
            aggregateFunctionName = aggregateFunctionName.charAt(0).toUpperCase() + aggregateFunctionName.slice(1);
            aggregateFunctionName = aggregateFunctionName.charAt(0).toUpperCase() + aggregateFunctionName.slice(1);
        return `${aggregateInfo['field']} ${aggregateFunctionName}`;
    }
    public getAggregateValue(allAggregates: AggregateDescriptor[], aggregateInfo: AggregateDescriptor): string {
        let aggregateValue: number = 0;
        let aggregateFieldData: any = allAggregates[aggregateInfo['field']];
        if (aggregateFieldData != null)
            aggregateValue = aggregateFieldData[aggregateInfo['aggregate']];
        return aggregateValue != null ? aggregateValue.toString() : '0';
    }

    public get DirectiveInputs(): FormTemplateInstancesDirectiveData {
        return this.directiveInputs;
    }

    public get GridMode(): string {
        return this.gridMode;
    }

    // All this does is return the value already set. It allows the use of innerHtml in the template so that a hyperlink can be rendered
    public getCellDisplayValue(row: object, columnDef: FormField, columnIndex: number): string {
        let name = columnDef.alias?.trim().length > 0 ? columnDef.alias : (columnDef.displayName || columnDef.name);
        return row[name];
    }
    public getGroupedCellDisplayValue(row: object, columnDef: FormField, columnIndex: number): string {
        return row['Key'];
    }
    public get FieldDisplayNames(): string[] {
        let displayNames: string[] = [];

        for (let index: number = 0; index < this.configuredColDefs.length; index++) {
            let colDef: FormField = this.configuredColDefs[index];
            let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(colDef.fieldDefinitionClassName);
            if (!fieldDef.isNumeric) {
                let displayName: string = (colDef.displayName != null) && (colDef.displayName.trim() != '') ? colDef.displayName : colDef.name;
                displayNames.push(displayName);
            }
        }

        return displayNames;
    }

    public getFilterType(columnDef: FormField): string {
        let handler: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName).customLogicHandler;
        let filterType = handler.filterType();
        return filterType;
    }

    public getFormat(columnDef: FormField): string {
        // TODO -- use IFieldDefinitionLogic
        if (columnDef.fieldDefinitionClassName.indexOf("Date") > -1) {
            return "d";
        } else {
            return "";
        }
    }

    public getColumnWidth(columnDef: FormField): number {
        if (columnDef.columnWidth) {
            return columnDef.columnWidth;
        } else {
            return 50;
        }
    }

    public ColumnIsFilterable(columnDef: FormField): boolean {
        if (!this.showGridFilter) return false;

        let handler: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName).customLogicHandler;
        let isFilterable: boolean = handler.isFilterable();
        let filterObject: FormTemplatePropertyInfo = null;

        return isFilterable && (filterObject == null);
    }

    public openFormInstance(dataItem: any) {
        if (this.formField.openFormInstancesInNewTab)
            window.open(dataItem.FormOpenUrl, "_blank");
        else
            this.router.navigateByUrl(dataItem.FormOpenUrl);
    }

    public openGroupedFormInstance(dataItem: any) {
        let formOpenUrl = `site/${dataItem.dataCollectionId}/structure/folder/${dataItem.folderId}/form/${dataItem.formInstanceId}`;
        if (this.formField.openFormInstancesInNewTab)
            window.open(formOpenUrl, "_blank");
        else
            this.router.navigateByUrl(formOpenUrl);
    }

    public subscribedIcon(dataItem: any) {
        if (dataItem.Subscription) {
            return "notifications_active";
        } else {
            return "notifications";
        }
    }

    public subscribedIconClass(dataItem: any) {
        if (dataItem.Subscription) {
            return 'material-icons';
        } else {
            return 'material-icons-outlined';
        }
    }

    public subscribedIconTitle(dataItem: any) {
        if (dataItem.Subscription) {
            return 'Unsubscribe';
        } else {
            return 'Subscribe to this Form Instance';
        }
    }

    public toggleSubscription(dataItem: any) {
        let workflowFilter = new SubscriptionFilter('FormInstanceFilter');
        workflowFilter.formInstanceId = dataItem.Id;
        workflowFilter.eventName = EventNameEnum.FormInstance_Workflow_Transition;

        let workflowSubscription = new EventSubscription();
        workflowSubscription.eventName = EventNameEnum.FormInstance_Workflow_Transition;
        workflowSubscription.dataCollectionId = this.currentSiteService.Site.id;
        workflowSubscription.subscribeeType = 'UserMaxId';
        workflowSubscription.filters = [];
        workflowSubscription.filters.push(workflowFilter);

        let manualFilter = new SubscriptionFilter('FormInstanceFilter');
        manualFilter.formInstanceId = dataItem.Id;
        manualFilter.eventName = EventNameEnum.FormInstance_Manually_Triggered;

        let manuallyTriggeredSubscription = new EventSubscription();
        manuallyTriggeredSubscription.eventName = EventNameEnum.FormInstance_Manually_Triggered;
        manuallyTriggeredSubscription.dataCollectionId = this.currentSiteService.Site.id;
        manuallyTriggeredSubscription.subscribeeType = 'UserMaxId';
        manuallyTriggeredSubscription.addFilter(manualFilter);

        // PJH - 10/22/2024 - Note: the value of dataItem.Subscription used to be the Id of a specific Subscription, but now it's the _count_ of subscriptions
        // for a given FormInstance. (A user can be subscribed to multiple events for the same FormInstance)
        if (dataItem.Subscription > 0) {
            workflowSubscription.id = dataItem.Subscription;
            this.eventSubscriptionService.unsubscribeFromFormInstance(dataItem.Id);
            dataItem.Subscription = null;
        }
        else {
            this.eventSubscriptionService.subscribe([workflowSubscription, manuallyTriggeredSubscription]).then(response => {
                dataItem.Subscription = response[0]?.id;
                if (!dataItem.Subscription) {
                    console.log('subscription ID not found in response');
                }
            });
        }
    }

    public toggleFavorite(dataItem: any) {
        dataItem.Favorite = !dataItem.Favorite;
        this.formInstanceService.toggleIsFavorite(dataItem.Id).then(fi => { });
    }

    public favIcon(dataItem: any) {
        if (dataItem.Favorite) {
            return "star";
        } else {
            return "star_outline";
        }
    }

    public favIconTitle(dataItem: any) {
        if (dataItem.Favorite) {
            return 'Unfavorite';
        } else {
            return 'Favorite this Form Instance';
        }
    }

    public toggleLinkFormInstance(dataItem: any) {
        let currentFormInstanceId = this.FormInstanceElement.formInstanceId;

        if (this.allowGridLinking(dataItem.Id)) {
            if (currentFormInstanceId <= 0 || dataItem.Id <= 0) {
                let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                    width: "400px",
                    data: new ConfirmationDialogModel(
                        `Add Form Instance Link`,
                        `Form Linking is not allowed on this page.`,
                        { hideNo: true, confirmButtonText: `Close` }
                    )
                });
                return;
            }
            dataItem.LinkedFormInstance = !dataItem.LinkedFormInstance;
            this.formInstanceService.toggleIsLinkedFormInstance(currentFormInstanceId, dataItem.Id).then(fi => { });
        }

    }

    /**
     * Displays the icon for the linking column of the grid
     * @param dataItem - The details for the current row
     */
    public linkFormInstanceIcon(dataItem: any) {
        if (this.allowGridLinking(dataItem.Id)) {
            if (dataItem.LinkedFormInstance) {
                return "link-chain";
            } else {
                return "unlink-chain";
            }
        }

        return "";
    }

    /**
     * Displays the title for the linking icon
     * @param dataItem - The details for the current row
     */
    public linkFormInstanceTitle(dataItem: any): string {
        if (this.allowGridLinking(dataItem.Id)) {
            if (dataItem.LinkedFormInstance) {
                return 'Unlink this Form Instance';
            } else {
                return 'Link this Form Instance';
            }
        }

        return "";
    }

    /**
     * Determines if form instances can be linked to the current form instance
     * @param rowFormInstanceId - The form instance id assigned to the data item's row
     */
    public allowGridLinking(rowFormInstanceId: number): boolean {
        let result: boolean = true;
        let currentFormInstanceId: number = this.FormInstanceElement.formInstanceId;

        //***Don't allow a form to link to itself (vnext-1592)
        if (currentFormInstanceId == null || currentFormInstanceId <= 0)
            result = false;
        else if (rowFormInstanceId == this.FormInstanceElement.formInstanceId)
            result = false;

        return result;
    }

    public handleFilterChange(event: any): void {
        var newState: State = TemplateInstancesGridStateManager.getGridState(this.browserStorageService, this.FormField);
        newState.filter.filters = event.filters;
        TemplateInstancesGridStateManager.saveGridState(this.browserStorageService, this.FormField, newState);
    }

    public CheckboxClass(): string {
        return "red";
    }

    public showActionButton(action: string): boolean {
        if (!this.templateInstancesButtons.buttonIsEnabled(action)) {
            return false;
        }

        let userHasNoWriteableFolders = this.WriteableFoldersCount == 0;
        let isPublishAction = action.indexOf('publish') > -1;
        if (userHasNoWriteableFolders && !isPublishAction) {
            return false;
        }

        let result: boolean = false;
        let noActionSelected = this.action == null;
        let buttonsShouldNotBeHiddenForSelectedAction = this.doNotHideButtonsForTheseActions.indexOf(this.action) > -1;
        let selectedActionMatchesActionButton = this.action === action;

        if (noActionSelected) {
            result = true;
        } else if (selectedActionMatchesActionButton) {
            result = true;
        } else if (buttonsShouldNotBeHiddenForSelectedAction) {
            result = true;
        } else {
            result = false;
        }

        return result;
    }

    public buttonName(action: string): string {
        return this.templateInstancesButtons.getButtonName(action);
    }

    public get CanCreateForm(): boolean {
        return (this.gridData != null) && this.gridData.canCreateForm;
    }
    public get CanCreateFolder(): boolean {
        return (this.gridData != null) && this.gridData.canCreateSubFolderInAtLeastOneFolder;
    }
    public get CanDeleteForm(): boolean {
        return (this.gridData != null) && this.gridData.canDeleteAtLeastOneForm;
    }
    public get CanMoveForm(): boolean {
        return (this.gridData != null) && this.gridData.canMoveAtLeastOneForm;
    }
    public get CanCopyForm(): boolean {
        return (this.gridData != null) && this.gridData.canCopyAtLeastOneForm;
    }

    public get FormRenameInProgress(): boolean {
        return this.formRenameInProgress;
    }

    public renameFormClick(id: number) {
        this.formRenameInProgress = true;
        setTimeout(() => {
            this.formNameInput.nativeElement.focus();
        }, 0);
    }

    public renameFormKeydown(event) {
        if (event.key === 'Enter') {
            if (event.target && event.target.value.trim().length > 0) {
                this.formRenameInProgress = false;
                this.renameForm(event.target.id, event.target.value.trim());
            }
        } else if (event.key === 'Escape') {
            this.formRenameInProgress = false;
        }
    }

    public renameForm(formId, formNewName) {
        let formToRename: FormInstance = new FormInstance();
        formToRename.name = formNewName;
        formToRename.setId(formId);

        this.formInstanceService.rename(formToRename).then(response => {
            this.remoteBindingDirectiveRef.rebind();
        });
    }

    public renameFormCancel() {
        this.formRenameInProgress = false;
    }

    public renameFormSave() {
        if (this.formNameInput.nativeElement.value.trim().length > 0 && this.formNameInput.nativeElement.id > 0) {
            this.formRenameInProgress = false;
            this.renameForm(this.formNameInput.nativeElement.id, this.formNameInput.nativeElement.value.trim());
        }
    }

    public showCancelButton() {
        return this.gridMode == 'manage' && this.actionRequiresFormSelection();
    }

    // Called when a user initially clicks on one of the action buttons to select an action to perform
    // Some actions get performed immediately and in those cases handlePerformActionClick() gets called from this method
    // Others require further intervention from the user, such as selecting Forms, and in those cases handlePerformActionClick()
    // is called upon clicking the "Continue" button
    public handleSelectActionClick(action: string): void {
        this.gridMode = HomePageActionsEnum.MANAGE_GRID_MODE;
        this.action = action;

        // Some actions require the further step of selecting Forms -- those actions are handled by handlePerformActionClick()
        if (!this.actionRequiresFormSelection())
            this.handlePerformActionClick();
    }

    // Called when, having initially clicked on a button, the user finally clicks the button (e.g. "Continue") that actually _performns_ the action
    public handlePerformActionClick(): void {
        // this first case is special and doesn't require a dialog - the user can only create Forms
        // in one Folder, so just go ahead and create it and open it
        if (this.action === HomePageActionsEnum.ACTION_CREATE_FORM && this.writeableFolders.length == 1) {
            let destinationFolderId = this.writeableFolders[0];
            let newFormInstance = new FormInstance();
            newFormInstance.folderId = destinationFolderId;
            newFormInstance.formId = this.formField.formTemplateId;
            newFormInstance.dataCollectionId = this.currentSiteService.Site.id;
            this.createAndOpenFormInstance(newFormInstance);
        } else if (this.action == HomePageActionsEnum.ACTION_PUBLISH_FORM) {
            this.processOpenPublishingDialogue();
        } else if (this.action == HomePageActionsEnum.ACTION_EXPORT_DATA) {
            this.openExportFormDataDialogue();
        } else if (this.action == HomePageActionsEnum.ACTION_BULK_WORKFLOW) {
            let initData = new BulkWorkflowDialogInitData(this.formField.formTemplateId); //VNEXT-1652 
            initData.formInstanceIds = this.SelectedRows.map(x => { return x.Id });
            let dialogRef = this.dialog.open(BulkWorkflowDialog, {
                height: 'auto',
                width: '400px;',
                data: initData
            });

            dialogRef.afterClosed().subscribe(x => {
                // Show all buttons again.
                this.action = null;
                this.gridMode = HomePageActionsEnum.VIEW_GRID_MODE;
                this.remoteBindingDirectiveRef.rebind();
            });
        } else if (this.action == HomePageActionsEnum.ACTION_SUBSCRIBE_ALL) {
            //TODO: VNEXT-810
        }
        else {
            this.processHomePageActionDialogue();
        }
    }

    private processOpenPublishingDialogue(): void {
        this.openDocPublishingDialog('forminstance', this.SelectedRows.map(x => { return x.Id }));
    }

    private processHomePageActionDialogue(): void {
        let data: HomePageActionDialogInitData = {
            siteId: this.currentSiteService.Site.id,
            siteName: this.currentSiteService.Site.name,
            selectedRows: this.SelectedRows,
            action: this.action,
            formId: this.formField.formTemplateId
        };
        let dialogRef = this.dialog.open(HomePageActionDialog, {
            width: '800px',
            data: data
        });

        dialogRef.afterClosed().subscribe(result => {
            this.initializeActionButtons();
            let returnObj = result as HomePageActionDialogReturnObj;
            if (returnObj?.success) {
                if (returnObj.formInstance) {
                    this.newFormInstanceCreated.emit(returnObj.formInstance);
                } else if (returnObj.folderToPublish) {
                    this.openDocPublishingDialog('folder', [returnObj.folderToPublish]);
                } else {
                    this.helper.reInit();
                    this.remoteBindingDirectiveRef.rebind();
                }
            }
        });
    }

    private openExportFormDataDialogue(): void {
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';

        let exportDataConfig: ExportDataDialogConfig = new ExportDataDialogConfig();
        exportDataConfig.action = ExportDataDialogAction.ExportTemplateInstances;
        exportDataConfig.objectId = this.formField.id;
        exportDataConfig.parameterObject = this.kendoGridData.gridState;
        let selectedFormInstanceIds = this.SelectedRows.map(x => { return x.Id });
        if (selectedFormInstanceIds.length == this.gridData.results.length) {
            exportDataConfig.allFormsSelected = true;
        } else {
            exportDataConfig.formInstanceIds = selectedFormInstanceIds;
        }

        dialogConfig.data = exportDataConfig;

        const dialogRef = this.dialog.open(ExportDataDialog, dialogConfig);
        dialogRef.afterClosed().subscribe(exportedFormInstance => {
            if (!exportedFormInstance) {
                this.initializeActionButtons();
            }
        });
    }

    private openDocPublishingDialog(entityType: string, entityIds: number[]) {
        this.documentPublishingService.getDocumentPublishingSettingsForSite(this.currentSiteService.Site.id, true).then(x => {
            let data = {
                settings: x,
                entityType: entityType,
                entityId: null,
                entityIds: null
            };

            if (entityType == 'folder') {
                data.entityId = entityIds[0];
            } else {
                data.entityIds = entityIds;
            }

            let dialogRef = this.dialog.open(DocumentPublishingPickerDialog, {
                width: '800px',
                data: data
            });
        });
    }

    public handleCancelActionClick(): void {
        this.initializeActionButtons();
    }

    public get ActionButtonClass() {
        return this.gridMode == HomePageActionsEnum.MANAGE_GRID_MODE ? 'action-btn-greyed-out' : 'action-btn';
    }

    public get ShowCheckboxes() {
        return this.gridMode == HomePageActionsEnum.MANAGE_GRID_MODE;
    }

    public get ShowNoColumnsWarning() {
        return (this.ColumnDefs == null || this.ColumnDefs.length == 0);
    }

    public get UserClickedOnNameColumn() {
        return this.userClickedOnNameColumn;
    }

    // Define methods related to the grid's context menu.
    public onCellClick(e: any): void {
        this.userClickedOnNameColumn = e.column?.field == 'Name';

        // Call the original code associated with a cell click.
        this.helper.setIndexOfClickedRow(e);
        this.rowIndexClicked = e.rowIndex;

        // If this event is of type 'contextmenu', handle that.
        if (e.type === "contextmenu") {
            const originalEvent = e.originalEvent;
            originalEvent.preventDefault();

            this.helper.ContextMenuDataItem = e.dataItem;

            // Show the context menu.
            this.gridContextMenu.show({
                left: originalEvent.pageX,
                top: originalEvent.pageY,
            });
        }
    }

    public onContextMenuSelect(eventData: any): void {
        if ((this.helper.ContextMenuDataItem != null) && (eventData['item'] != null) && (eventData['item']['text'] != null)) {
            let menuItemText: string = eventData['item']['text'];
            switch (menuItemText) {
                case Open_MenuItem:
                    this.openFormInstance(this.helper.ContextMenuDataItem);
                    break;
                case CopyLink_MenuItem:
                    this.clipboard.copy(`${location.origin}/${this.helper.ContextMenuDataItem.FormOpenUrl}`);
                    break;
                case Delete_MenuItem:
                case Copy_MenuItem:
                case Move_MenuItem:
                    if (menuItemText == Delete_MenuItem)
                        this.action = 'delete';
                    else if (menuItemText == Copy_MenuItem)
                        this.action = 'copy';
                    else if (menuItemText == Move_MenuItem)
                        this.action = 'move';

                    this.selectedRows = [this.helper.ContextMenuDataItem];
                    this.processHomePageActionDialogue();
                    break;
                case PublishForm_MenuItem:
                    this.selectedRows = [this.helper.ContextMenuDataItem];
                    this.processOpenPublishingDialogue();
                    break;
                case WorkflowTransition_MenuItem:
                    // Note:  this action is not yet implemented.
                    break;
                default:
                    break;
            }
        }
    }
    // End methods related to the grid's context menu.

    // Helper methods.
    private createAndOpenFormInstance(newFormInstance: FormInstance) {
        this.message = "Creating and Opening New Form based on this Form Template...";
        this.formInstanceService.create(newFormInstance).then(x => {
            this.formInstanceService.getContent(x.id, false).then(x => {
                this.newFormInstanceCreated.emit(x);
                this.message = null;
            });
        });
    }

    // Reads the saved field/column configs of a Home Page Grid out of the Home Page Grid Field's formTemplateInstancesGridJsonConfig property.
    // It populates this.configuredColDefs with those columns that have the isSelected flag set
    // How this.configuredColDefs is used:
    //  1)  the template loops these ColDefs to build the columns of the grid
    //  2)  it's passed to the remote directive which encapsulates the logic to load / filter / sort the Grid. (Maybe this isn't actually needed - needs analysis but maybe
    //      the server can do it all, since it also parses FormField.FormTemplateInstancesGridJsonConfig)
    private retrieveFormTemplateAndColumnDefs(): void {
        if (this.formField.formTemplateId) {
            // PJH - 4/4/2025 - I removed the optional param to getFields() as that param should either be zero or the id of a grid field
            this.formTemplateInstancesService.getFields(this.formField.formTemplateId).then(formFields => {
                if ((this.formField.formTemplateInstancesGridJsonConfig != null) && (this.formField.formTemplateInstancesGridJsonConfig.length > 0)) {
                    this.configuredColumnNames = [];
                    
                    // This is where we grab the stored column names representing the columns the Grid is supposed to display
                    let jsonConfig: FormTemplateInstancesConfig = FormTemplateInstancesConfig.fromJsonString(this.formField.formTemplateInstancesGridJsonConfig);
                    this.newConfiguredColDefs.buildColDefs(this.fieldDefinitionService, formFields, jsonConfig);

                    let properties = jsonConfig.SelectedFormTemplateProperties;
                    for (let index: number = 0; index < properties.length; index++) {
                        let propName = properties[index].propertyName;
                        let fieldId = properties[index].fieldId;
                        let alias = properties[index].alias;
                        let associatedFlexFieldName: string = properties[index].associatedFlexFieldName;
                        this.configuredColumnNames.push(propName);
                        let field = this.getField(fieldId, propName, alias, formFields, associatedFlexFieldName);
                        if (field) {
                            this.configuredColDefs.push(field);

                            let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(field.fieldDefinitionClassName);
                            if (fieldDefinition.isNumeric) {
                                let aggregateNames: string = properties[index].optionalAggregateNames;
                                if ((aggregateNames != null) && (aggregateNames.trim() != '')) {
                                    // Add any valid, non-duplicate aggregates.
                                    this.addAnyNumericFieldAggregates(propName, aggregateNames);
                                }
                            }
                        }
                    } // for

                    // Set directive input values.
                    this.directiveInputs.formTemplateId = this.FormTemplateId;
                    this.directiveInputs.columnDefinitions = this.ColumnDefs;
                    this.directiveInputs.formFieldId = this.formField.id;
                    this.directiveInputs.aggregates = this.Aggregates;
                    this.directiveInputs.formsGridOrdersJson = this.formField.formsGridOrdersJson;
                    this.directiveInputs.formInstanceId = this.FormInstanceElement.formInstanceId;
                    //console.log('retrieveFormTemplateAndColumnDefs found formsGridOrdersJson:');
                    //console.log(this.directiveInputs.formsGridOrdersJson);

                    // 07-02-2024 note:  defer the querying of data if this field has important pending conditional logic.
                    this.directiveInputs.canQueryData = !this.formField.hasPendingConditionalLogic;
                    if ((jsonConfig.SelectedGridFieldId != null) && (!isNaN(jsonConfig.SelectedGridFieldId))) {
                        // Note:  changing the value of this.selectedGridId to null can cause the dreaded "value changed
                        //        while template was being evaluated" (or whatever the precise error message reads).
                        this.directiveInputs.selectedGridId = jsonConfig.SelectedGridFieldId;
                    } // if
                } // if

                this.fieldsInitialized = true;
            });
        } else {
            // Note:  this.formTemplate is not used in the rest of this component.
        }
    }

    private addAnyNumericFieldAggregates(propertyName: string, aggregateNames: string): void {
        let splitAggregateNames: string[] = aggregateNames.split('|');
        for (let index: number = 0; index < splitAggregateNames.length; index++) {
            let aggregateName: string = splitAggregateNames[index];
            if (this.allowableAggregateNames.indexOf(aggregateName) >= 0) {
                let validAggregateName: 'count' | 'sum' | 'average' | 'min' | 'max' = <'count' | 'sum' | 'average' | 'min' | 'max'>aggregateName;
                let aggregate: AggregateDescriptor = {
                    field: propertyName,
                    aggregate: validAggregateName
                };
                this.aggregates.push(aggregate);
            }
        }
    }

    // Try and get the FormField from the dummy metadata fields first, then from the Form Template itself
    private getField(fieldId: number, propName: string, alias: string, fields: FormField[], associatedFlexFieldName: string): FormField {
        let field = this.getMetadataField(propName);

        if (!field) {
            var key = `${fieldId}_${propName}`;
            field = fields.filter(x => { return `${x.id}_${x.displayName || x.name}` == key })[0];
        }
        if (alias?.trim().length > 0) {
            field.alias = alias;
        }
        if (associatedFlexFieldName?.trim().length > 0) {
            field.associatedFlexFieldName = associatedFlexFieldName;
        }
        return field;
    }

    private getMetadataField(propName: string): FormField {
        let field = this.metadataDummyFormFields.filter(f => { return f.name == propName || f.displayName == propName });
        return field[0];
    }

    private initializeActionButtons() {
        this.gridMode = HomePageActionsEnum.VIEW_GRID_MODE;
        this.action = null;
        this.selectedRows = [];
        this.selectAllState = 'unchecked';
        this.indexOfSelectedFormToPublish = null;
    }

    // Maybe the actionPerformers currently instatiated in the dialog could be instatiated
    // in this class instead and passed to the dialog -- then they could answer the question being answered here
    private actionRequiresFormSelection() {
        return this.action &&
            this.action != HomePageActionsEnum.ACTION_CREATE_FORM &&
            this.action != HomePageActionsEnum.ACTION_CREATE_FOLDER &&
            this.action != HomePageActionsEnum.ACTION_PUBLISH_FOLDER &&
            this.action != HomePageActionsEnum.ACTION_SUBSCRIBE_ALL;
    }

    private restoreAnySavedGridState(directive: FormTemplateInstancesGridRemoteBindingDirective): void {
        let savedState: State = TemplateInstancesGridStateManager.getGridState(this.browserStorageService, this.FormField);
        //console.log('restoreAnySavedGridState found saved state:');
        //console.log(savedState);

        if (savedState != null) {
            // Restore this state.
            if (savedState.sort != null) {
                this.kendoGridData.gridState.sort = savedState.sort;
            }
            if (savedState.filter != null) {
                this.kendoGridData.gridState.filter = savedState.filter;
                if (savedState.filter.filters?.length > 0) this.showGridFilter = true;
            }
            directive.restoreSavedState(savedState);
        }
    }

    private applyFilterIfApplicable(): boolean {
        let canClearInterval: boolean = false;

        if (this.remoteBindingDirectiveRef == null) {
        } else if (this.kendoGridData.calledRebindAfterAppliedFilters || (!this.remoteBindingDirectiveRef.canRebind())) {
        } else {
            if (!this.kendoGridData.calledRebindAfterAppliedFilters) {
                if (true || this.remoteBindingDirectiveRef.canRebind()) {
                    if ((this.kendoGridData.appliedFilters != null) && (this.kendoGridData.appliedFilters.length > 0)) {
                        for (let index: number = 0; index < this.kendoGridData.appliedFilters.length; index++) {
                            let appliedFilter: FilterDescriptor = this.kendoGridData.appliedFilters[index];
                            if (!FormTemplateInstancesComponent.filterExists(appliedFilter, this.kendoGridData.gridState.filter.filters)) {
                                this.kendoGridData.gridState.filter.filters.push(appliedFilter);
                            }
                        }

                        this.remoteBindingDirectiveRef.makeSureFiltersApplied(<FilterDescriptor[]>this.kendoGridData.gridState.filter.filters);
                    }

                    this.remoteBindingDirectiveRef.CanQueryData = true;
                    this.remoteBindingDirectiveRef.rebind();

                    this.kendoGridData.calledRebindAfterAppliedFilters = true;
                    canClearInterval = true;
                }
            } else {
                canClearInterval = true;
            }
        }

        return canClearInterval;
    }
}
